import React, { useState, useEffect } from "react";
import axios from "axios";
import { Input, Select, Radio, Button, Form, message } from "antd";
import AppBar from "@mui/material/AppBar";
import List from "@mui/material/List";
import Grid from "@mui/material/Grid";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const rolesApi = process.env.REACT_APP_API_Roles;
const teamsURL = process.env.REACT_APP_API_Team;
const manualTestersApi = process.env.REACT_APP_API_Testers;
const specialTestersApi = process.env.REACT_APP_API_CreateSpecialTesters;

const AddTesterForm = ({ setOpen }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [testerRoles, setTesterRoles] = useState([]);
  const [cnic, setCnic] = useState(""); // State for CNIC

  useEffect(() => {
    fetchTeams();
    fetchTesterRoles();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const response = await axios.get(teamsURL);
      setTeams(response.data.body);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTesterRoles = async () => {
    try {
      const response = await axios.get(rolesApi);
      const data = response.data?.data ?? response.data?.body ?? [];
      const allRoles = Array.isArray(data) ? data : [];
      const testerOnly = allRoles.filter(
        (r) => r.id && (r.is_tester === 1 || r.is_tester === true)
      );
      setTesterRoles(testerOnly);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  // Handle change for CNIC field to ensure only numeric input and max length of 13 digits
  const handleCnicChange = (e) => {
    const value = e.target.value;
    // Only allow digits and a maximum of 13 digits
    if (/^\d{0,13}$/.test(value)) {
      setCnic(value);
      form.setFieldsValue({ cnic: value });
    }
  };

  const handleSubmit = async (values) => {
    try {
      const selectedTeam = teams.find((t) => t.name === values.team);
      if (!selectedTeam) {
        throw new Error("Selected team not found.");
      }

      const roleId = Number(values.role_id);
      const selectedRole = testerRoles.find((r) => Number(r.id) === roleId);
      if (!selectedRole) {
        message.error("Please select a valid role.");
        return;
      }

      const userDb = selectedRole.user_db || "manual";
      const team_id = selectedTeam.id;

      const payload = {
        ...values,
        role_id: roleId,
        team_id,
        cnic: values.cnic,
      };

      if (userDb === "special") {
        const response = await axios.post(specialTestersApi, payload);

        if (
          response.status === 200 &&
          response.data.statusCode !== 400 &&
          response.data.statusCode !== 401
        ) {
          setOpen(false);
          message.success("Special Tester added successfully");
        } else if (response.status === 200 && response.data.statusCode === 400) {
          message.error("Failed to add Special Tester. Email already exists");
        } else if (response.status === 200 && response.data.statusCode === 401) {
          message.error("Failed to add Special Tester. CNIC already exists");
        } else {
          message.error("Failed to add Special Tester.");
        }
      } else {
        const response = await axios.post(manualTestersApi, payload);

        if (response.data?.body?.statusCode === 400) {
          message.error("Email already exists, please use a different email.");
        } else if (response.data?.body?.statusCode === 401) {
          message.error("CNIC already exists, please use a different CNIC.");
        } else if (response.status === 200) {
          setOpen(false);
          message.success("Tester added successfully.");
        } else {
          message.error("Failed to add tester.");
        }
      }
    } catch (error) {
      console.error("Error adding tester:", error);

      if (
        error.response &&
        error.response.data?.message === "Email already exists."
      ) {
        message.error("Email already exists, please use a different email.");
      } else {
        message.error("Failed to add tester.");
      }
    }
  };

  return (
    <>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            Add Tester
          </Typography>
          <IconButton edge="end" color="inherit" onClick={() => setOpen(false)} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <List style={{ height: "100vh" }}>
        <Grid
          container
          style={{ height: "100%", justifyContent: "center" }}
          alignItems="center"
        >
          <Grid item xs={12} sm={8} md={6} lg={8}>
            <Form
              form={form}
              onFinish={handleSubmit}
              initialValues={{
                gender: "male", // set default gender if needed
              }}
              className="mx-10"
            >
              <div className=" mb-4">
                <h2 className="text-2xl font-bold">Add Tester</h2>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <Form.Item
                  name="role_id"
                  rules={[
                    { required: true, message: "Please select Role" },
                  ]}
                >
                  <Select
                    placeholder="Select Role"
                    dropdownStyle={{ zIndex: 9999 }}
                    showSearch
                    optionFilterProp="label"
                    options={testerRoles.map((r) => ({
                      value: r.id,
                      label: `${r.title || `Role ${r.id}`} (${r.user_db || "manual"})`,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="first_name"
                  rules={[
                    { required: true, message: "Please enter First Name" },
                  ]}
                >
                  <Input
                    placeholder="Enter First Name"
                    addonBefore="First Name"
                  />
                </Form.Item>
                <Form.Item
                  name="last_name"
                  rules={[
                    { required: true, message: "Please enter Last Name" },
                  ]}
                >
                  <Input
                    placeholder="Enter Last Name"
                    addonBefore="Last Name"
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Please enter Email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="Enter Email" addonBefore="Email" />
                </Form.Item>
                <Form.Item
                  name="contact_number"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Contact Number",
                    },
                    {
                      pattern: /^(?:\+?92|0)\d{10}$/,
                      message: "Please enter a valid contact number",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Contact Number"
                    addonBefore="Contact Number"
                  />
                </Form.Item>
                {/* New CNIC Field (not required) */}
                <Form.Item
                  name="cnic"
                  rules={[
                    {
                      pattern: /^\d{13}$/,
                      message: "CNIC must be exactly 13 digits if provided",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter CNIC"
                    addonBefore="CNIC"
                    value={cnic}
                    onChange={handleCnicChange}
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                    rules={[
                      { required: true, message: "Please enter Password" },
                      {
                        min: 6,
                        message: "Password must be at least 6 characters long",
                      },
                    ]}
                  >
                    <Input.Password
                      placeholder="Enter Password"
                      addonBefore="Password"
                      iconRender={(visible) =>
                        visible ? <AiOutlineEyeInvisible /> : <AiOutlineEye />
                      }
                    />
                  </Form.Item>
                <Form.Item
                  name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Passwords do not match")
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      placeholder="Confirm Password"
                      addonBefore="Confirm Password"
                      iconRender={(visible) =>
                        visible ? <AiOutlineEyeInvisible /> : <AiOutlineEye />
                      }
                    />
                  </Form.Item>
                <Form.Item
                  name="gender"
                  rules={[{ required: true, message: "Please select Gender" }]}
                >
                  <Radio.Group>
                    <Radio value="male">Male</Radio>
                    <Radio value="female">Female</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name="team"
                  rules={[{ required: true, message: "Please select Team" }]}
                >
                  <Select
                    placeholder="Select Team"
                    dropdownStyle={{ zIndex: 9999 }}
                  >
                    {teams.map((team) => (
                      <Select.Option key={team.id} value={team.name}>
                        {team.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="bank_name"
                  rules={[
                    { required: true, message: "Please enter Bank Name" },
                  ]}
                >
                  <Input
                    placeholder="Enter Bank Name"
                    addonBefore="Bank Name"
                  />
                </Form.Item>
                <Form.Item
                  name="account_title"
                  rules={[
                    { required: true, message: "Please enter Account Title" },
                  ]}
                >
                  <Input
                    placeholder="Enter Account Title"
                    addonBefore="Account Title"
                  />
                </Form.Item>
                <Form.Item
                  name="account_number"
                  rules={[
                    { required: true, message: "Please enter Account Number" },
                    {
                      max: 35,
                      message: "Account number must be of valid length",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Account Number"
                    addonBefore="Account Number"
                  />
                </Form.Item>
              </div>
              <div className="flex justify-end">
                <Button
                  type="secondary"
                  className="bg-[blue] text-[white]"
                  htmlType="submit"
                >
                  Add Tester
                </Button>
              </div>
            </Form>
          </Grid>
        </Grid>
      </List>
    </>
  );
};

export default AddTesterForm;
